import type {
  ResourceKind,
  ResourceLifecycleStatus,
  ResourceNode,
  ResourceRelationship,
  ResourceRelationshipType,
  ResourceTopologyHistoryItem,
  ResourceTopologyImportCommitResult,
  ResourceTopologyImportMode,
  ResourceTopologyImportPreview,
  ResourceTopologyImportPreviewRow,
  ResourceTopologyImportRowStatus,
  ResourceTopologyImportScope,
  ResourceTopologyImportSummary,
  ResourceTopologyIssue,
  ResourceTopologyVersion,
  ResourceTopologyVersionStatus,
  ResourceTopologyWorkspace,
  TopologyIssueHardness,
  TopologyIssueSeverity,
} from '../types/prodResourceTopology';

type RawRecord = Record<string, unknown>;

const asRecord = (value: unknown): RawRecord => (
  value && typeof value === 'object' ? value as RawRecord : {}
);

const pick = (raw: RawRecord, ...keys: string[]): unknown => {
  for (const key of keys) {
    if (raw[key] !== undefined) return raw[key];
  }
  return undefined;
};

const asString = (value: unknown, fallback = ''): string => (
  typeof value === 'string' ? value : value == null ? fallback : String(value)
);

const asNullableString = (value: unknown): string | null => {
  if (value == null || value === '') return null;
  return String(value);
};

const asNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asNullableNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const VERSION_STATUSES: ResourceTopologyVersionStatus[] = [
  'DRAFT', 'VALIDATED', 'PUBLISHED', 'SUPERSEDED', 'ABANDONED',
];

const RESOURCE_KINDS: ResourceKind[] = [
  'BUILDING', 'AREA', 'SUITE', 'ROOM', 'EQUIPMENT', 'COMPONENT', 'CIP_STATION', 'CIP_LOOP',
];

const LIFECYCLE_STATUSES: ResourceLifecycleStatus[] = ['ACTIVE', 'INACTIVE', 'RETIRED'];
const RELATIONSHIP_TYPES: ResourceRelationshipType[] = [
  'PARENT_OF', 'CIP_LOOP_MEMBER', 'CIP_PRIMARY_STATION', 'CIP_BACKUP_STATION',
];
const ISSUE_SEVERITIES: TopologyIssueSeverity[] = ['info', 'warning', 'critical'];
const ISSUE_HARDNESS: TopologyIssueHardness[] = ['hard', 'soft'];

const enumValue = <T extends string>(value: unknown, values: readonly T[], fallback: T): T => {
  const normalized = asString(value).toUpperCase() as T;
  return values.includes(normalized) ? normalized : fallback;
};

const issueSeverity = (value: unknown): TopologyIssueSeverity => {
  const normalized = asString(value).toLowerCase() as TopologyIssueSeverity;
  return ISSUE_SEVERITIES.includes(normalized) ? normalized : 'critical';
};

const issueHardness = (value: unknown): TopologyIssueHardness => {
  const normalized = asString(value).toLowerCase() as TopologyIssueHardness;
  return ISSUE_HARDNESS.includes(normalized) ? normalized : 'hard';
};

export const unwrapTopologyData = (payload: unknown): unknown => {
  const raw = asRecord(payload);
  return raw.data ?? payload;
};

export const mapResourceTopologyVersion = (value: unknown): ResourceTopologyVersion => {
  const raw = asRecord(value);
  return {
    id: asString(pick(raw, 'id', 'version_id', 'versionId')),
    facilityCode: asString(pick(raw, 'facility_code', 'facilityCode'), 'F1'),
    versionNo: asNumber(pick(raw, 'version_number', 'version_no', 'versionNumber', 'versionNo')),
    name: asString(pick(raw, 'name', 'version_name', 'versionName'), '未命名版本'),
    status: enumValue(pick(raw, 'status'), VERSION_STATUSES, 'DRAFT'),
    revision: asNumber(pick(raw, 'revision')),
    validatedRevision: asNullableNumber(pick(raw, 'validated_revision', 'validatedRevision')),
    baseVersionId: asNullableString(pick(raw, 'base_version_id', 'baseVersionId')),
    createdAt: asNullableString(pick(raw, 'created_at', 'createdAt')),
    createdBy: asNullableString(pick(raw, 'created_by', 'createdBy')),
    validatedBy: asNullableString(pick(raw, 'validated_by', 'validatedBy')),
    publishedBy: asNullableString(pick(raw, 'published_by', 'publishedBy')),
    validatedAt: asNullableString(pick(raw, 'validated_at', 'validatedAt')),
    publishedAt: asNullableString(pick(raw, 'published_at', 'publishedAt')),
  };
};

export const mapResourceNode = (value: unknown): ResourceNode => {
  const raw = asRecord(value);
  return {
    id: asString(pick(raw, 'id', 'resource_id', 'resourceId')),
    code: asString(pick(raw, 'code', 'resource_code', 'resourceCode')),
    name: asString(pick(raw, 'name', 'resource_name', 'resourceName')),
    kind: enumValue(pick(raw, 'kind', 'resource_kind', 'resourceKind'), RESOURCE_KINDS, 'EQUIPMENT'),
    lifecycleStatus: enumValue(
      pick(raw, 'lifecycle_status', 'lifecycleStatus'),
      LIFECYCLE_STATUSES,
      'ACTIVE',
    ),
    attributes: (() => {
      const attributes = pick(raw, 'attributes');
      return attributes && typeof attributes === 'object' && !Array.isArray(attributes)
        ? attributes as Record<string, unknown>
        : null;
    })(),
    note: asNullableString(pick(raw, 'note')),
  };
};

export const mapResourceRelationship = (value: unknown): ResourceRelationship => {
  const raw = asRecord(value);
  const type = enumValue(
    pick(raw, 'type', 'relation_type', 'relationType', 'relationship_type', 'relationshipType'),
    RELATIONSHIP_TYPES,
    'PARENT_OF',
  );
  return {
    id: asString(pick(raw, 'id', 'relationship_id', 'relationshipId')),
    type,
    sourceResourceId: asString(pick(raw, 'source_resource_id', 'sourceResourceId')),
    targetResourceId: asString(pick(raw, 'target_resource_id', 'targetResourceId')),
    priority: asNullableNumber(pick(raw, 'priority')),
    note: asNullableString(pick(raw, 'note')),
  };
};

export const mapResourceTopologyIssue = (value: unknown, index = 0): ResourceTopologyIssue => {
  const raw = asRecord(value);
  const constraintCode = asString(pick(raw, 'constraint_code', 'constraintCode'), 'TOPOLOGY_INVALID');
  return {
    id: asString(pick(raw, 'id', 'issue_id', 'issueId'), `${constraintCode}-${index}`),
    constraintCode,
    severity: issueSeverity(pick(raw, 'severity')),
    hardOrSoft: issueHardness(pick(raw, 'hard_or_soft', 'hardOrSoft')),
    violationReason: asString(
      pick(raw, 'violation_reason', 'violationReason', 'message'),
      '资源拓扑校验未通过',
    ),
    violationMessageTemplate: asNullableString(
      pick(raw, 'violation_message_template', 'violationMessageTemplate'),
    ),
    affectedEntities: (() => {
      const entities = pick(raw, 'affected_entities', 'affectedEntities');
      if (!Array.isArray(entities)) return [];
      return entities.map((entity) => {
        const item = asRecord(entity);
        return {
          entityType: asString(pick(item, 'entity_type', 'entityType'), 'RESOURCE'),
          entityId: asString(pick(item, 'entity_id', 'entityId')),
        };
      }).filter((entity) => entity.entityId);
    })(),
    suggestedAction: asNullableString(pick(raw, 'suggested_action', 'suggestedAction')),
    ...(pick(raw, 'sheet') ? { sheet: asString(pick(raw, 'sheet')) } : {}),
    ...(asNullableNumber(pick(raw, 'row_number', 'rowNumber')) !== null
      ? { rowNumber: asNumber(pick(raw, 'row_number', 'rowNumber')) }
      : {}),
  };
};

export const mapResourceTopologyWorkspace = (payload: unknown): ResourceTopologyWorkspace => {
  const raw = asRecord(unwrapTopologyData(payload));
  const relationships = pick(raw, 'relationships', 'resource_relationships', 'resourceRelationships');
  const issues = pick(raw, 'issues', 'validation_issues', 'validationIssues');
  return {
    version: mapResourceTopologyVersion(pick(raw, 'version', 'topology_version', 'topologyVersion')),
    resources: Array.isArray(raw.resources) ? raw.resources.map(mapResourceNode) : [],
    relationships: Array.isArray(relationships) ? relationships.map(mapResourceRelationship) : [],
    issues: Array.isArray(issues) ? issues.map(mapResourceTopologyIssue) : [],
  };
};

export const mapResourceTopologyHistoryItem = (value: unknown): ResourceTopologyHistoryItem => {
  const raw = asRecord(value);
  return {
    ...mapResourceTopologyVersion(raw),
    changeSummary: asNullableString(pick(raw, 'change_summary', 'changeSummary')),
  };
};

const IMPORT_ROW_STATUSES: ResourceTopologyImportRowStatus[] = ['CREATE', 'UPDATE', 'ERROR'];
const IMPORT_MODES: ResourceTopologyImportMode[] = ['APPEND', 'REPLACE'];
const IMPORT_SCOPES: ResourceTopologyImportScope[] = ['FULL', 'RESOURCES', 'RELATIONSHIPS'];

export const mapResourceTopologyImportSummary = (value: unknown): ResourceTopologyImportSummary => {
  const raw = asRecord(value);
  return {
    resourceRows: asNumber(pick(raw, 'resource_rows', 'resourceRows')),
    relationshipRows: asNumber(pick(raw, 'relationship_rows', 'relationshipRows', 'relationRows')),
    createNodes: asNumber(pick(raw, 'create_nodes', 'createNodes')),
    updateNodes: asNumber(pick(raw, 'update_nodes', 'updateNodes')),
    createRelationships: asNumber(pick(raw, 'create_relationships', 'createRelationships', 'createRelations')),
    updateRelationships: asNumber(pick(raw, 'update_relationships', 'updateRelationships', 'updateRelations')),
    errorCount: asNumber(pick(raw, 'error_count', 'errorCount')),
    warningCount: asNumber(pick(raw, 'warning_count', 'warningCount')),
  };
};

export const mapResourceTopologyImportPreviewRow = (
  value: unknown,
  index = 0,
): ResourceTopologyImportPreviewRow => {
  const raw = asRecord(value);
  const sheet = asString(pick(raw, 'sheet'), '工作簿');
  const rowNumber = asNumber(pick(raw, 'row_number', 'rowNumber'));
  const status = enumValue(pick(raw, 'status'), IMPORT_ROW_STATUSES, 'ERROR');
  const entityTypeValue = asString(pick(raw, 'entity_type', 'entityType')).toUpperCase();
  return {
    id: asString(pick(raw, 'id'), `${sheet}-${rowNumber}-${index}`),
    sheet,
    rowNumber,
    status,
    entityType: entityTypeValue === 'RELATION' ? 'RELATION' : 'RESOURCE',
    code: asNullableString(pick(raw, 'code')),
    constraintCode: asNullableString(pick(raw, 'constraint_code', 'constraintCode')),
    message: asNullableString(pick(raw, 'message', 'violation_reason', 'violationReason')),
  };
};

export const mapResourceTopologyImportPreview = (value: unknown): ResourceTopologyImportPreview => {
  const raw = asRecord(unwrapTopologyData(value));
  const rows = pick(raw, 'rows');
  const issues = pick(raw, 'issues');
  return {
    previewToken: asString(pick(raw, 'preview_token', 'previewToken')),
    baseRevision: asNumber(pick(raw, 'base_revision', 'baseRevision')),
    mode: enumValue(pick(raw, 'mode'), IMPORT_MODES, 'APPEND'),
    scope: enumValue(pick(raw, 'scope'), IMPORT_SCOPES, 'FULL'),
    summary: mapResourceTopologyImportSummary(pick(raw, 'summary')),
    rows: Array.isArray(rows) ? rows.map(mapResourceTopologyImportPreviewRow) : [],
    issues: Array.isArray(issues) ? issues.map(mapResourceTopologyIssue) : [],
  };
};

export const mapResourceTopologyImportCommitResult = (value: unknown): ResourceTopologyImportCommitResult => {
  const raw = asRecord(unwrapTopologyData(value));
  return {
    version: mapResourceTopologyVersion(pick(raw, 'version')),
    summary: mapResourceTopologyImportSummary(pick(raw, 'summary')),
  };
};
